import type {
  FrameTime,
  ListEntry,
  MediaState,
  PropertyDescriptor,
  SourceContext,
  SourceRuntime,
  SourceTypeDefinition,
  VideoProvider,
} from '../core/definitions';
import type { SettingsBag } from '../core/model';
import { BaseSourceRuntime, bool, num, str } from './base';
import { hasFrameStats, type FrameStats } from './frameStats';
import { ImageRuntime, SNIFF_BYTES, sniffImageType } from './image';
import { MediaRuntime, MEDIA_DEFAULTS } from './media';
import { buildOrder, isUrlRef, kindFromUrl, nextCycleOrder } from './playlistOrder';
import { SlideshowBlend, fadeProgress, fillsStage, stageView, type Size, type StageFit } from './slideshowBlend';

export type PlaylistRepeat = 'off' | 'all' | 'one';
export type PlaylistTransition = 'cut' | 'fade';

/** `value` is an asset id or an http(s)/blob/data URL. `durationMs` > 0 overrides the image duration, and cuts a clip short. */
export type PlaylistEntry = ListEntry & { durationMs?: number };

export type PlaylistSettings = {
  items: PlaylistEntry[];
  repeat: PlaylistRepeat;
  shuffle: boolean;
  imageDurationMs: number;
  transition: PlaylistTransition;
  transitionMs: number;
  /** Pause progression and playback while not on program; resume where it was. */
  pauseWhenHidden: boolean;
  /** How every item fills the stage, in steady state and during fades. */
  fit: StageFit;
  /** Output size; 0 takes the size of the first picture shown, kept from then on (and across reloads). */
  stageWidth: number;
  stageHeight: number;
};

const DEFAULTS: PlaylistSettings = {
  items: [],
  repeat: 'all',
  shuffle: false,
  imageDurationMs: 5000,
  transition: 'fade',
  transitionMs: 500,
  pauseWhenHidden: false,
  fit: 'stretch',
  stageWidth: 0,
  stageHeight: 0,
};

const MAX_DURATION_MS = 3_600_000;

function readEntry(raw: unknown, index: number): PlaylistEntry | null {
  // Settings version 0 stored bare asset ids; their ids must stay stable across update() calls.
  if (typeof raw === 'string') return raw ? { id: `item-${index}`, value: raw } : null;
  if (!raw || typeof raw !== 'object') return null;
  const e = raw as Record<string, unknown>;
  const value = str(e.value, '');
  if (!value) return null;
  const entry: PlaylistEntry = { ...(e as SettingsBag), id: str(e.id, '') || `item-${index}`, value };
  const duration = num(e.durationMs, 0, 0, MAX_DURATION_MS);
  if (duration > 0) entry.durationMs = Math.round(duration);
  else delete entry.durationMs;
  return entry;
}

function read(s: Partial<PlaylistSettings> & { loop?: unknown }): PlaylistSettings {
  const items: PlaylistEntry[] = [];
  const seen = new Set<string>();
  if (Array.isArray(s.items)) {
    s.items.forEach((raw, i) => {
      const entry = readEntry(raw, i);
      if (!entry) return;
      if (seen.has(entry.id)) entry.id = `${entry.id}-${i}`;
      seen.add(entry.id);
      items.push(entry);
    });
  }
  const repeat =
    s.repeat === 'off' || s.repeat === 'all' || s.repeat === 'one'
      ? s.repeat
      : typeof s.loop === 'boolean'
        ? s.loop
          ? 'all'
          : 'off'
        : DEFAULTS.repeat;
  return {
    items,
    repeat,
    shuffle: bool(s.shuffle, DEFAULTS.shuffle),
    imageDurationMs: Math.round(num(s.imageDurationMs, DEFAULTS.imageDurationMs, 100, MAX_DURATION_MS)),
    transition: s.transition === 'cut' || s.transition === 'fade' ? s.transition : DEFAULTS.transition,
    transitionMs: Math.round(num(s.transitionMs, DEFAULTS.transitionMs, 0, 10_000)),
    pauseWhenHidden: bool(s.pauseWhenHidden, DEFAULTS.pauseWhenHidden),
    fit: s.fit === 'contain' || s.fit === 'cover' || s.fit === 'stretch' ? s.fit : DEFAULTS.fit,
    stageWidth: Math.round(num(s.stageWidth, 0, 0, 16_384)),
    stageHeight: Math.round(num(s.stageHeight, 0, 0, 16_384)),
  };
}

/** Upgrades version 0 (`items: string[]`, `loop: boolean`) and repairs any stored bag. */
export function migratePlaylistSettings(settings: unknown): PlaylistSettings {
  const bag = settings && typeof settings === 'object' ? (settings as Partial<PlaylistSettings> & { loop?: unknown }) : {};
  // Version 0 entries get positional ids ('item-N'), the same ones read() derives, so a bag that is
  // migrated and one that is not name the same entries; read() renames any id that repeats.
  return read(bag);
}

interface Slot {
  entry: PlaylistEntry;
  /** The play order this slot belongs to (a reshuffled next cycle has its own) and its position in it. */
  order: number[];
  position: number;
  kind: 'image' | 'media';
  runtime: SourceRuntime | null;
  gain: GainNode;
  unsubscribe: () => void;
  failed: boolean;
}

class PlaylistRuntime extends BaseSourceRuntime<PlaylistSettings> {
  private cfg: PlaylistSettings;
  private order: number[] = [];
  private position = 0;
  private current: Slot | null = null;
  /** The next item, loaded ahead so the switch never shows an empty frame. */
  private incoming: Slot | null = null;
  /** Switch to `incoming` as soon as it has a picture. */
  private switchPending = false;
  private outgoing: Slot | null = null;
  private fade: { startedAt: number; durationMs: number; timer: ReturnType<typeof setTimeout> } | null = null;
  private readonly blend = new SlideshowBlend();
  /** Steady-state picture for an item whose aspect differs from the stage under 'contain' or 'cover'. */
  private readonly still = new SlideshowBlend();
  private stillKey = '';
  /** Steady-state picture for an item that fills the stage as it is: its own texture, laid out at the stage size. */
  private readonly view = stageView(() => this.current?.runtime?.getVideo() ?? null, () => this.stage() ?? { width: 1, height: 1 });
  /** Automatic stage size: the first picture shown, then kept. */
  private autoStage: Size | null = null;
  private failures = 0;
  private finished = false;
  private active = false;
  private userPaused = false;
  private stopped = false;
  private lastSkip = '';
  private rasterScale: number | null = null;
  /** The reshuffled order that follows `cycleOf`, made once so preloading and advancing agree on it. */
  private cycle: { of: number[]; next: number[] } | null = null;
  /** Display timer of the current item (images, and clips with a duration). */
  private timer: ReturnType<typeof setTimeout> | null = null;
  private timerStartedAt = 0;
  private timerRemaining = 0;
  private timerTotal = 0;
  // Slots come and go; the engine connects to this stable node once.
  private readonly out: GainNode;

  constructor(ctx: SourceContext, settings: Partial<PlaylistSettings>, runtimeState?: SettingsBag) {
    super(ctx);
    this.cfg = read(settings);
    this.out = ctx.audioContext.createGain();
    this.setAudio(this.out);
    this.order = buildOrder(this.cfg.items.length, this.cfg.shuffle);
    const resumeId = typeof runtimeState?.entryId === 'string' ? runtimeState.entryId : null;
    const sw = num(runtimeState?.stageWidth, 0, 0, 16_384);
    const sh = num(runtimeState?.stageHeight, 0, 0, 16_384);
    if (sw > 0 && sh > 0) this.autoStage = { width: sw, height: sh };
    const resumeIndex = resumeId ? this.cfg.items.findIndex((e) => e.id === resumeId) : -1;
    this.start(resumeIndex >= 0 ? Math.max(0, this.order.indexOf(resumeIndex)) : 0);
  }

  override getFrameStats(): FrameStats {
    const child = this.current?.runtime;
    return child && hasFrameStats(child) ? child.getFrameStats() : super.getFrameStats();
  }

  /** Continuous only while a clip is playing; still images and pauses legitimately deliver no new pictures. */
  expectsContinuousFrames(): boolean {
    const child = this.current?.runtime;
    if (!child || !hasFrameStats(child) || typeof child.expectsContinuousFrames !== 'function') return false;
    return child.expectsContinuousFrames();
  }

  override getVideo(): VideoProvider | null {
    const to = this.current?.runtime?.getVideo() ?? null;
    if (!to) return null;
    const stage = this.stage() ?? { width: to.width, height: to.height };
    const fade = this.fade;
    const from = fade ? this.outgoing?.runtime?.getVideo() : null;
    if (fade && from) {
      this.blend.draw(from, to, fadeProgress(performance.now(), fade.startedAt, fade.durationMs), stage, this.cfg.fit);
      return this.blend.provider;
    }
    if (fillsStage(this.cfg.fit, to, stage)) return this.view;
    const key = `${to.version}|${stage.width}x${stage.height}|${this.cfg.fit}`;
    if (to.dynamic || key !== this.stillKey) {
      this.still.draw(null, to, 1, stage, this.cfg.fit);
      this.stillKey = key;
    }
    return this.still.provider;
  }

  /** The stage every item is fitted into: the settings, else the automatic size once known. */
  private stage(): Size | null {
    if (this.cfg.stageWidth > 0 && this.cfg.stageHeight > 0) return { width: this.cfg.stageWidth, height: this.cfg.stageHeight };
    return this.autoStage;
  }

  private saveState(): void {
    const entryId = this.current?.entry.id;
    if (!entryId) return;
    const state: SettingsBag = { entryId };
    if (this.autoStage) {
      state.stageWidth = this.autoStage.width;
      state.stageHeight = this.autoStage.height;
    }
    this.ctx.saveRuntimeState(state);
  }

  getMediaState(): MediaState | null {
    const slot = this.current;
    if (!slot?.runtime) return null;
    const index = this.cfg.items.indexOf(slot.entry);
    const child = slot.runtime.getMediaState?.() ?? null;
    let state: MediaState['state'];
    if (this.stopped) state = 'stopped';
    else if (this.finished) state = 'ended';
    else if (this.userPaused || this.hiddenPaused()) state = 'paused';
    else state = child?.state === 'buffering' ? 'buffering' : 'playing';
    const durationMs = child ? child.durationMs : (slot.entry.durationMs ?? this.cfg.imageDurationMs);
    const timeMs = child ? child.timeMs : Math.max(0, durationMs - this.remainingMs());
    return { state, timeMs, durationMs, index: Math.max(0, index), count: this.cfg.items.length };
  }

  getLiveInfo(): Record<string, string> {
    const slot = this.current;
    const count = this.cfg.items.length;
    return {
      item: slot && count ? `Item ${this.cfg.items.indexOf(slot.entry) + 1} of ${count}` : 'Nothing playing',
      skipped: this.lastSkip,
    };
  }

  update(settings: PlaylistSettings): void {
    if (this.destroyed) return;
    const prev = this.cfg;
    const next = read(settings);
    this.cfg = next;
    const key = (items: PlaylistEntry[]) => items.map((e) => `${e.id}\u0000${e.value}`).join('\n');
    if (key(next.items) !== key(prev.items) || next.shuffle !== prev.shuffle) {
      this.relist();
      return;
    }
    // Same list: carry the current entry's new per-item fields over without reloading it.
    const slot = this.current;
    if (slot) {
      const fresh = next.items.find((e) => e.id === slot.entry.id);
      if (fresh) slot.entry = fresh;
      if (this.itemDurationMs() !== this.timerTotal) this.retime();
    }
    if (next.repeat !== prev.repeat) this.preloadNext();
    if (next.pauseWhenHidden !== prev.pauseWhenHidden) this.applyRunning();
  }

  tick(time: FrameTime): void {
    if (this.destroyed) return;
    this.current?.runtime?.tick?.(time);
    this.outgoing?.runtime?.tick?.(time);
  }

  setActive(active: boolean): void {
    if (this.destroyed || this.active === active) return;
    this.active = active;
    this.current?.runtime?.setActive?.(active);
    this.applyRunning();
  }

  setRasterScale(scale: number): void {
    this.rasterScale = scale;
    this.current?.runtime?.setRasterScale?.(scale);
    this.incoming?.runtime?.setRasterScale?.(scale);
  }

  invoke(action: string): void {
    if (this.destroyed) return;
    switch (action) {
      case 'next':
        this.advance(1, true);
        break;
      case 'previous':
        this.advance(-1, true);
        break;
      case 'restart':
        this.userPaused = false;
        this.stopped = false;
        this.restartList();
        break;
      case 'retry':
        this.restartList();
        break;
      case 'play':
        this.stopped = false;
        this.userPaused = false;
        if (this.finished) this.restartList();
        else this.applyRunning();
        break;
      case 'pause':
        this.userPaused = true;
        this.applyRunning();
        break;
      case 'stop':
        this.userPaused = true;
        this.stopped = true;
        this.restartList();
        break;
    }
  }

  private hiddenPaused(): boolean {
    return this.cfg.pauseWhenHidden && !this.active;
  }

  private running(): boolean {
    return !this.userPaused && !this.hiddenPaused() && !this.finished;
  }

  /** Starts or holds the current item's playback and timer to match the pause flags. */
  private applyRunning(): void {
    const slot = this.current;
    if (!slot?.runtime) return;
    if (this.running()) {
      // An ended clip waiting for the next item to load keeps its last frame rather than replaying.
      if (slot.kind === 'media' && slot.runtime.getStatus().state !== 'ended') slot.runtime.invoke?.('play');
      this.resumeTimer();
    } else {
      if (slot.kind === 'media') slot.runtime.invoke?.(this.stopped ? 'stop' : 'pause');
      this.pauseTimer();
    }
    this.emit();
  }

  private restartList(): void {
    this.order = buildOrder(this.cfg.items.length, this.cfg.shuffle);
    this.failures = 0;
    this.finished = false;
    this.lastSkip = '';
    this.disposeSlot(this.incoming);
    this.incoming = null;
    this.start(0);
  }

  /** The list or its order changed: keep the current entry playing if it is still listed. */
  private relist(): void {
    const keepId = this.current?.entry.id;
    this.order = buildOrder(this.cfg.items.length, this.cfg.shuffle);
    this.disposeSlot(this.incoming);
    this.incoming = null;
    this.switchPending = false;
    const keepValue = this.current?.entry.value;
    const keptIndex = keepId === undefined ? -1 : this.cfg.items.findIndex((e) => e.id === keepId && e.value === keepValue);
    if (keptIndex >= 0 && this.current) {
      this.position = this.order.indexOf(keptIndex);
      this.current.entry = this.cfg.items[keptIndex];
      this.current.order = this.order;
      this.current.position = this.position;
      if (this.itemDurationMs() !== this.timerTotal) this.retime();
      this.preloadNext();
      return;
    }
    this.failures = 0;
    this.start(0);
  }

  private start(position: number): void {
    this.finished = false;
    if (this.order.length === 0) {
      this.finishFade();
      this.disposeSlot(this.current);
      this.current = null;
      this.clearTimer();
      this.setStatus('idle');
      this.emit();
      return;
    }
    const slot = this.current;
    if (slot && slot.entry === this.cfg.items[this.order[position]]) {
      slot.order = this.order;
      slot.position = this.position = position;
      this.restartCurrent();
      this.preloadNext();
      return;
    }
    if (!this.current) this.setStatus('starting');
    this.load(this.order, position, true);
  }

  /** Step from the current position. Returns null at the end of a non-repeating list. */
  /**
   * Step from the item on screen or, while a switch is pending, from the item about to replace it,
   * so a quick Next/Previous is relative to what the operator will see. Null at the end of a
   * non-repeating list.
   */
  private step(direction: 1 | -1, manual: boolean): { order: number[]; position: number } | null {
    const from = this.switchPending && this.incoming && !this.incoming.failed ? this.incoming : { order: this.order, position: this.position };
    const { order } = from;
    const length = order.length;
    if (length === 0) return null;
    const wraps = this.cfg.repeat !== 'off' || manual;
    const next = from.position + direction;
    if (next >= length) {
      if (!wraps) return null;
      return { order: this.orderAfter(order), position: 0 };
    }
    if (next < 0) return wraps ? { order, position: length - 1 } : { order, position: 0 };
    return { order, position: next };
  }

  private advance(direction: 1 | -1, manual = false): void {
    if (this.destroyed) return;
    // Already moving on: an ended clip re-reports 'ended' while its successor loads.
    if (!manual && this.switchPending && this.incoming) return;
    if (!manual && this.cfg.repeat === 'one') {
      this.restartCurrent();
      return;
    }
    const target = this.step(direction, manual);
    if (!target) {
      this.finished = true;
      this.clearTimer();
      this.setStatus('ended');
      this.emit();
      return;
    }
    this.finished = false;
    const incoming = this.incoming;
    if (incoming && !incoming.failed && direction === 1 && incoming.position === target.position && sameOrder(incoming.order, target.order)) {
      this.switchPending = true;
      if (this.isReady(incoming)) this.swap();
      return;
    }
    if (this.current && target.order === this.current.order && target.position === this.current.position) {
      this.restartCurrent();
      return;
    }
    this.load(target.order, target.position, true);
  }

  private restartCurrent(): void {
    const slot = this.current;
    this.finished = false;
    if (!slot?.runtime) return;
    if (slot.kind === 'media') slot.runtime.invoke?.('restart');
    this.startTimer();
    this.applyRunning();
  }

  /** Loads the item at `position` of `order` as the incoming slot; `switchWhenReady` makes it current once it has a picture. */
  private load(order: number[], position: number, switchWhenReady: boolean): void {
    this.disposeSlot(this.incoming);
    const entry = this.cfg.items[order[position]];
    const slot: Slot = {
      entry,
      order,
      position,
      kind: 'image',
      runtime: null,
      gain: this.ctx.audioContext.createGain(),
      unsubscribe: () => {},
      failed: false,
    };
    slot.gain.gain.value = 0;
    slot.gain.connect(this.out);
    this.incoming = slot;
    this.switchPending = switchWhenReady;
    void this.createChild(slot);
  }

  private async createChild(slot: Slot): Promise<void> {
    const ref = slot.entry.value;
    let kind: 'image' | 'media';
    if (isUrlRef(ref)) {
      kind = kindFromUrl(ref);
    } else {
      const blob = await this.ctx.assets.getBlob(ref).catch(() => null);
      if (slot !== this.incoming || this.destroyed) return;
      if (!blob) {
        this.onSlotFailed(slot, 'the file is missing from this browser');
        return;
      }
      // The stored type can be empty or generic (older imports), so the bytes decide.
      const head = new Uint8Array(await blob.slice(0, SNIFF_BYTES).arrayBuffer().catch(() => new ArrayBuffer(0)));
      if (slot !== this.incoming || this.destroyed) return;
      kind = sniffImageType(head) || blob.type.startsWith('image/') ? 'image' : 'media';
    }
    slot.kind = kind;
    const asset = isUrlRef(ref) ? { assetId: '', url: ref } : { assetId: ref, url: '' };
    const runtime: SourceRuntime =
      kind === 'image'
        ? new ImageRuntime(this.ctx, asset)
        // Repeat belongs to the playlist, so each clip plays once and reports 'ended'. It loads paused
        // and starts when it becomes current.
        : new MediaRuntime(this.ctx, { ...MEDIA_DEFAULTS, ...asset, loop: false, autoplay: false });
    slot.runtime = runtime;
    runtime.getAudio()?.connect(slot.gain);
    runtime.setActive?.(this.active);
    if (this.rasterScale !== null) runtime.setRasterScale?.(this.rasterScale);
    slot.unsubscribe = runtime.subscribe(() => this.onSlot(slot));
    this.onSlot(slot);
  }

  private isReady(slot: Slot): boolean {
    const rt = slot.runtime;
    if (!rt || slot.failed) return false;
    const state = rt.getStatus().state;
    if (state !== 'live' && state !== 'ended') return false;
    return slot.kind === 'media' || rt.getVideo() !== null;
  }

  private onSlot(slot: Slot): void {
    if (this.destroyed) return;
    const status = slot.runtime?.getStatus();
    if (slot === this.incoming) {
      if (status?.state === 'error') this.onSlotFailed(slot, status.error?.message ?? 'it could not be played');
      else if (this.switchPending && this.isReady(slot)) this.swap();
      return;
    }
    if (slot === this.current) {
      if (status?.state === 'ended' && slot.kind === 'media' && !this.finished) {
        this.advance(1);
        return;
      }
      if (status?.state === 'error') {
        this.onSlotFailed(slot, status.error?.message ?? 'it stopped playing');
        return;
      }
      this.emit();
      return;
    }
    if (slot === this.outgoing) this.emit();
  }

  private onSlotFailed(slot: Slot, reason: string): void {
    slot.failed = true;
    const label = `item ${this.cfg.items.indexOf(slot.entry) + 1}`;
    this.lastSkip = `Skipped ${label}: ${reason}`;
    this.log('warn', `playlist ${label} skipped: ${reason}`);
    this.failures++;
    const wasCurrent = slot === this.current;
    const wasPending = slot === this.incoming && this.switchPending;
    if (this.failures >= this.cfg.items.length) {
      this.disposeSlot(this.incoming);
      this.incoming = null;
      this.finishFade();
      this.disposeSlot(this.current);
      this.current = null;
      this.clearTimer();
      this.fail('load-failed', 'No item in the playlist could be played');
      return;
    }
    if (slot === this.incoming) {
      // Skip past it: load the item after it, keeping whatever is on screen meanwhile.
      const after = this.stepFrom(slot.order, slot.position);
      this.disposeSlot(slot);
      this.incoming = null;
      if (after) this.load(after.order, after.position, wasPending);
      else if (wasPending) {
        this.finished = true;
        this.clearTimer();
        this.setStatus(this.current ? 'ended' : 'error', this.current ? undefined : { code: 'load-failed', message: 'No item in the playlist could be played' });
      }
      this.emit();
      return;
    }
    if (wasCurrent) {
      this.position = slot.position;
      this.advance(1, true);
    }
  }

  /** Position after `position` in `order` for preloading and skipping; null past the end of a non-repeating list. */
  private stepFrom(order: number[], position: number): { order: number[]; position: number } | null {
    const next = position + 1;
    if (next < order.length) return { order, position: next };
    if (this.cfg.repeat === 'off') return null;
    return { order: this.orderAfter(order), position: 0 };
  }

  /** Play order of the cycle after `order`: the same order, or one fresh reshuffle per cycle. */
  private orderAfter(order: number[]): number[] {
    if (!this.cfg.shuffle || order.length < 2) return order;
    if (this.cycle?.of !== order) this.cycle = { of: order, next: nextCycleOrder(order) };
    return this.cycle.next;
  }

  private preloadNext(): void {
    const slot = this.current;
    if (!slot || this.destroyed) return;
    const target = this.stepFrom(slot.order, slot.position);
    if (!target || (target.order.length === 1 && target.order[target.position] === slot.order[slot.position])) {
      this.disposeSlot(this.incoming);
      this.incoming = null;
      return;
    }
    const existing = this.incoming;
    if (existing && existing.position === target.position && sameOrder(existing.order, target.order) && existing.entry === this.cfg.items[target.order[target.position]]) return;
    this.load(target.order, target.position, false);
  }

  private swap(): void {
    const next = this.incoming;
    if (!next?.runtime) return;
    this.incoming = null;
    this.switchPending = false;
    this.finishFade();
    const prev = this.current;
    this.current = next;
    this.order = next.order;
    this.position = next.position;
    this.failures = 0;
    this.finished = false;
    const now = this.ctx.audioContext.currentTime;
    const fadeMs = this.cfg.transition === 'fade' ? this.cfg.transitionMs : 0;
    if (prev?.runtime && fadeMs > 0) {
      this.outgoing = prev;
      const durationMs = fadeMs;
      this.fade = { startedAt: performance.now(), durationMs, timer: setTimeout(() => this.finishFade(), durationMs) };
      rampGain(prev.gain, now, 0, fadeMs);
      rampGain(next.gain, now, 1, fadeMs);
    } else {
      this.disposeSlot(prev);
      next.gain.gain.cancelScheduledValues(now);
      next.gain.gain.value = 1;
    }
    this.startTimer();
    this.applyRunning();
    const picture = next.runtime.getVideo();
    if (!this.autoStage && picture) this.autoStage = { width: picture.width, height: picture.height };
    this.saveState();
    this.setStatus('live');
    this.emit();
    this.preloadNext();
  }

  private finishFade(): void {
    const fade = this.fade;
    if (!fade && !this.outgoing) return;
    if (fade) clearTimeout(fade.timer);
    this.fade = null;
    const out = this.outgoing;
    this.outgoing = null;
    this.disposeSlot(out);
    if (this.current) {
      const g = this.current.gain.gain;
      g.cancelScheduledValues(this.ctx.audioContext.currentTime);
      g.value = 1;
    }
    this.emit();
  }

  /** Display time of the current item: images always, clips only when the entry sets a duration. */
  private itemDurationMs(): number {
    const slot = this.current;
    if (!slot) return 0;
    if (slot.kind === 'image') return slot.entry.durationMs ?? this.cfg.imageDurationMs;
    return slot.entry.durationMs ?? 0;
  }

  private remainingMs(): number {
    return this.timer !== null ? Math.max(0, this.timerRemaining - (performance.now() - this.timerStartedAt)) : this.timerRemaining;
  }

  private startTimer(): void {
    this.clearTimer();
    this.timerTotal = this.timerRemaining = this.itemDurationMs();
  }

  /** The current item's duration setting changed: keep the time already shown and apply the new total. */
  private retime(): void {
    const elapsed = Math.max(0, this.timerTotal - this.remainingMs());
    const total = this.itemDurationMs();
    this.clearTimer();
    this.timerTotal = total;
    // An item already past its new duration moves on at once.
    this.timerRemaining = total > 0 ? Math.max(1, total - elapsed) : 0;
    this.resumeTimer();
  }

  private resumeTimer(): void {
    if (this.timer !== null || this.timerRemaining <= 0 || !this.running()) return;
    this.timerStartedAt = performance.now();
    this.timer = setTimeout(() => {
      this.timer = null;
      this.timerRemaining = 0;
      this.advance(1);
    }, this.timerRemaining);
  }

  private pauseTimer(): void {
    if (this.timer === null) return;
    this.timerRemaining = this.remainingMs();
    clearTimeout(this.timer);
    this.timer = null;
  }

  private clearTimer(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.timerRemaining = 0;
    this.timerTotal = 0;
  }

  private disposeSlot(slot: Slot | null): void {
    if (!slot) return;
    slot.unsubscribe();
    slot.runtime?.getAudio()?.disconnect();
    slot.runtime?.destroy();
    slot.runtime = null;
    slot.gain.disconnect();
    if (slot === this.current) this.current = null;
    if (slot === this.incoming) this.incoming = null;
  }

  protected release(): void {
    this.still.dispose();
    this.clearTimer();
    if (this.fade) clearTimeout(this.fade.timer);
    this.fade = null;
    this.disposeSlot(this.outgoing);
    this.outgoing = null;
    this.disposeSlot(this.incoming);
    this.disposeSlot(this.current);
    this.blend.dispose();
    this.out.disconnect();
  }
}

function sameOrder(a: readonly number[], b: readonly number[]): boolean {
  return a === b || (a.length === b.length && a.every((v, i) => v === b[i]));
}

function rampGain(gain: GainNode, now: number, to: number, ms: number): void {
  const param = gain.gain;
  param.cancelScheduledValues(now);
  param.setValueAtTime(param.value, now);
  param.linearRampToValueAtTime(to, now + ms / 1000);
}

export const playlistDefinition: SourceTypeDefinition<PlaylistSettings> = {
  kind: 'playlist',
  displayName: 'Playlist',
  caps: { video: true, audio: true, retry: true, controllableMedia: true },
  category: 'media',
  // One rotation shared by every item that shows it; a copy would run a second, unsynchronised one.
  duplicate: 'reference',
  settingsVersion: 1,
  migrate: (settings) => migratePlaylistSettings(settings),
  defaults: () => ({ ...DEFAULTS, items: [] }),
  properties: (): PropertyDescriptor[] => [
    {
      key: 'items',
      label: 'Files',
      type: 'list',
      item: 'asset',
      accept: 'image/*,video/*,audio/*',
      itemLabel: 'File',
      reorderable: true,
      allowDuplicates: true,
      fields: [{ key: 'durationMs', label: 'Duration (0 = default; clips play to the end)', type: 'duration', min: 0, max: MAX_DURATION_MS }],
    },
    {
      key: 'repeat',
      label: 'Repeat',
      type: 'select',
      options: [
        { value: 'off', label: 'Off: stop after the last item' },
        { value: 'all', label: 'All: start again from the first item' },
        { value: 'one', label: 'One: repeat the current item' },
      ],
    },
    { key: 'shuffle', label: 'Shuffle', type: 'boolean' },
    { key: 'imageDurationMs', label: 'Image duration', type: 'duration', min: 100, max: MAX_DURATION_MS },
    {
      key: 'transition',
      label: 'Between items',
      type: 'select',
      options: [
        { value: 'fade', label: 'Cross-fade' },
        { value: 'cut', label: 'Cut' },
      ],
    },
    { key: 'transitionMs', label: 'Fade length', type: 'number', min: 0, max: 10_000, step: 50, unit: 'ms', visibleWhen: (s) => s.transition !== 'cut' },
    { key: 'pauseWhenHidden', label: 'Pause when not on program', type: 'boolean' },
    {
      key: 'fit',
      label: 'Fit items',
      type: 'select',
      options: [
        { value: 'stretch', label: 'Stretch to the output size' },
        { value: 'contain', label: 'Fit inside (transparent bars)' },
        { value: 'cover', label: 'Fill (crop the edges)' },
      ],
      section: 'Output',
    },
    { key: 'stageWidth', label: 'Output width (0 = first item)', type: 'number', min: 0, max: 16_384, step: 1, unit: 'px', section: 'Output' },
    { key: 'stageHeight', label: 'Output height (0 = first item)', type: 'number', min: 0, max: 16_384, step: 1, unit: 'px', section: 'Output' },
    { key: 'nowPlaying', label: 'Now playing', type: 'info', liveKey: 'item' },
    { key: 'skippedInfo', label: 'Skipped', type: 'info', liveKey: 'skipped' },
    { key: 'previous', label: 'Previous', type: 'button', action: 'previous', section: 'Transport' },
    { key: 'next', label: 'Next', type: 'button', action: 'next', section: 'Transport' },
    { key: 'play', label: 'Play', type: 'button', action: 'play', section: 'Transport' },
    { key: 'pause', label: 'Pause', type: 'button', action: 'pause', section: 'Transport' },
    { key: 'restart', label: 'Restart playlist', type: 'button', action: 'restart', section: 'Transport' },
  ],
  create: (ctx, settings, runtimeState) => new PlaylistRuntime(ctx, settings, runtimeState),
};
